import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './OrderPage.css';
import { UIContext } from '../context/UIContext';
import { getWorkOrders, getMachineryStatus, getWorkOrderMachines } from '../api';
import OrderCard from '../components/Order/OrderCard';
import OrderListHeader from '../components/Order/OrderListHeader';
import Pagination from '../components/Pagination/Pagination';
import useMachineryWebSocket from '../hooks/useMachineryWebSocket';

const PAGE_SIZE = 20;
const emptyStats = {
  total: 0,
  urgent: 0,
  waiting: 0,
  processing: 0,
  completed: 0,
  assigned: 0,
  unassigned: 0,
};

const legacyStats = (orders) => ({
  total: orders.length,
  urgent: orders.filter((order) => order.urgent).length,
  waiting: orders.filter((order) => order.status === '대기중' || !order.status).length,
  processing: orders.filter((order) => order.status === '진행중').length,
  completed: orders.filter((order) => order.status === '완료').length,
  assigned: orders.filter((order) => order.machineName).length,
  unassigned: orders.filter((order) => !order.machineName).length,
});

const normalizeCount = (value) => (
  Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
);

const normalizeStats = (stats) => Object.fromEntries(
  Object.keys(emptyStats).map((key) => [key, normalizeCount(stats?.[key])]),
);

const newWorkOrderTemplate = {
  jobName: "",
  materialType: 1,
  materialShape: 1,
  materialWidth: 0,
  materialHeight: 0,
  inputLength: 0,
  inputNum: 0,
};

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function OrderPage() {
  const [orders, setOrders] = useState([]);
  const [machineOptions, setMachineOptions] = useState([]);
  const [liveMachines, setLiveMachines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all'); // New filter
  const today = getTodayString();
  const [startDate, setStartDate] = useState(() => sessionStorage.getItem('orderStartDate') || today);
  const [endDate, setEndDate] = useState(() => sessionStorage.getItem('orderEndDate') || today);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(emptyStats);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openWorkOrder } = useContext(UIContext);
  const { companyID } = useParams();
  const lastMessage = useMachineryWebSocket();
  const [refreshVersion, setRefreshVersion] = useState(0);
  const fetchGeneration = useRef(0);

  useEffect(() => () => {
    fetchGeneration.current += 1;
  }, []);

  const fetchOrders = useCallback(async () => {
    const generation = ++fetchGeneration.current;
    try {
      setIsLoading(true);
      const [fetchedOrders, fetchedOptions, fetchedLiveMachines] = await Promise.all([
        getWorkOrders({ 
          search: searchTerm, 
          searchField: searchField, 
          status: statusFilter,
          assigned: assignedFilter,
          startDate: startDate,
          endDate: endDate,
          page,
          pageSize: PAGE_SIZE,
        }),
        getWorkOrderMachines(),
        getMachineryStatus({ connected: true })
      ]);

      if (generation !== fetchGeneration.current) return;
      if (Array.isArray(fetchedOrders)) {
        setOrders(fetchedOrders);
        setTotalItems(fetchedOrders.length);
        setTotalPages(fetchedOrders.length > 0 ? page : 0);
        setStats(legacyStats(fetchedOrders));
      } else {
        const nextOrders = Array.isArray(fetchedOrders?.items) ? fetchedOrders.items : [];
        const nextTotalItems = normalizeCount(fetchedOrders?.pagination?.totalItems);
        const nextTotalPages = normalizeCount(fetchedOrders?.pagination?.totalPages);
        const lastValidPage = Math.max(1, nextTotalPages);

        if (page > lastValidPage) {
          setPage(lastValidPage);
          return;
        }

        setOrders(nextOrders);
        setTotalItems(nextTotalItems);
        setTotalPages(nextTotalPages);
        setStats(normalizeStats(fetchedOrders?.stats));
      }
      setMachineOptions(fetchedOptions || []);
      setLiveMachines(fetchedLiveMachines || []);
      setError(null);
    } catch (err) {
      if (generation !== fetchGeneration.current) return;
      setError('데이터를 불러오는 데 실패했습니다.');
      console.error(err);
      throw err;
    } finally {
      if (generation === fetchGeneration.current) setIsLoading(false);
    }
  }, [searchTerm, searchField, statusFilter, assignedFilter, startDate, endDate, page]);

  useEffect(() => {
    void fetchOrders().catch(() => {});
  }, [companyID, fetchOrders, refreshVersion]);

  // 웹소켓 메시지 수신 시 갱신
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'WORK_ORDER_UPDATE') {
      console.log("Work order update received via WebSocket, refreshing...");
      setRefreshVersion((version) => version + 1);
    }
  }, [lastMessage]);

  const updateStartDate = (value) => {
    setStartDate(value);
    setPage(1);
    sessionStorage.setItem('orderStartDate', value);
  };

  const updateEndDate = (value) => {
    setEndDate(value);
    setPage(1);
    sessionStorage.setItem('orderEndDate', value);
  };

  const handleOpenNewOrder = () => {
    openWorkOrder(newWorkOrderTemplate, { isMachineSelectable: true, machineOptions });
  };

  return (
    <div className="order-page-background">
      <div className="order-main-container">
        {/* 상단 타이틀 바: 검색 및 생성 버튼을 이쪽으로 이동 */}
        <div className="order-title-bar">
          <h1>작 업 지 시 서</h1>
          <div className="header-controls">
            <div className="date-filter-group">
                <input aria-label="시작일" type="date" value={startDate} onChange={(e) => updateStartDate(e.target.value)} className="order-date-input" />
                <span>~</span>
                <input aria-label="종료일" type="date" value={endDate} onChange={(e) => updateEndDate(e.target.value)} className="order-date-input" />
            </div>
            <div className="search-group">
                <select 
                    className="search-field-select"
                    aria-label="검색 필드"
                    value={searchField}
                    onChange={(e) => { setSearchField(e.target.value); setPage(1); }}
                >
                    <option value="all">전체</option>
                    <option value="jobName">작업명</option>
                    <option value="materialType">소재종류</option>
                    <option value="materialShape">소재모양</option>
                    <option value="inputLength">절단길이</option>
                    <option value="machineName">장비명</option>
                </select>
                <input
                    type="text"
                    aria-label="작업지시 검색어"
                    placeholder="검색어..."
                    className="order-search-input"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
            </div>
            <div className="filter-group">
                {/* 할당 필터 추가 */}
                <select
                    className="order-filter-select"
                    aria-label="할당 필터"
                    value={assignedFilter}
                    onChange={(e) => { setAssignedFilter(e.target.value); setPage(1); }}
                    style={{ marginRight: '5px' }}
                >
                    <option value="all">할당(전체)</option>
                    <option value="true">할당됨</option>
                    <option value="false">미할당</option>
                </select>

                <select
                    className="order-filter-select"
                    aria-label="상태 필터"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                    <option value="all">상태(전체)</option>
                    <option value="작성중">작성중</option>
                    <option value="대기중">대기중</option>
                    <option value="진행중">진행중</option>
                    <option value="완료">완료</option>
                </select>
            </div>
            <button className="add-order-btn" onClick={handleOpenNewOrder}>+ 작업지시서</button>
          </div>
        </div>

        {/* 상태별 통계 대시보드 (슬림하게 수정) */}
        <div className="order-dashboard">
            <div className="stat-item total">
                <span className="stat-label">전체</span>
                <span className="stat-value">{error ? '-' : stats.total}</span>
            </div>
            <div className="stat-item urgent">
                <span className="stat-label">긴급</span>
                <span className="stat-value">{error ? '-' : stats.urgent}</span>
            </div>
            <div className="stat-item processing">
                <span className="stat-label">진행중</span>
                <span className="stat-value">{error ? '-' : stats.processing}</span>
            </div>
            <div className="stat-item waiting">
                <span className="stat-label">대기</span>
                <span className="stat-value">{error ? '-' : stats.waiting}</span>
            </div>
            <div className="stat-item assigned">
                <span className="stat-label">할당</span>
                <span className="stat-value">{error ? '-' : stats.assigned}</span>
            </div>
            <div className="stat-item unassigned">
                <span className="stat-label">미할당</span>
                <span className="stat-value">{error ? '-' : stats.unassigned}</span>
            </div>
            <div className="stat-item completed">
                <span className="stat-label">완료</span>
                <span className="stat-value">{error ? '-' : stats.completed}</span>
            </div>
        </div>

        {/* 리스트 헤더 */}
        <OrderListHeader />

        {/* 지시서 카드 목록 */}
        <div className="order-list-wrapper">
          {error ? (
            <div className="error-msg" role="alert">{error}</div>
          ) : isLoading && orders.length === 0 ? (
            <div className="loading-msg">데이터 로딩 중...</div>
          ) : orders.length > 0 ? (
            orders.map((order) => (
              <OrderCard 
                key={order.id} 
                order={order} 
                machineOptions={machineOptions}
                liveMachines={liveMachines}
                refreshData={fetchOrders}
              />
            ))
          ) : (
            <div className="no-data-msg">등록된 작업 지시가 없습니다.</div>
          )}
        </div>
        {!error && <Pagination page={page} totalPages={totalPages} totalItems={totalItems} onPageChange={setPage} />}
      </div>
    </div>
  );
}

export default OrderPage;